import { apiUrl } from "config/api";
import { getDeviceHeaders, getDeviceInfo } from "helpers/deviceInfo";

const ACCESS_TOKEN_KEY = "accessToken";
const ACCESS_EXPIRY_KEY = "accessTokenExpiry";
const USER_ID_KEY = "userId";

export class ApiError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

const notify = (eventName, detail) => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  }
};

const parseResponse = async (response) => {
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) return null;
  return response.json();
};

const errorMessage = (payload, status) =>
  payload?.message || payload?.error || `Request failed with status ${status}`;

export const getStoredAccessToken = () => sessionStorage.getItem(ACCESS_TOKEN_KEY);

export const storeSession = ({ accessToken, accessTokenExpiresAt, userId }, { notifySession = true } = {}) => {
  sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  sessionStorage.setItem(ACCESS_EXPIRY_KEY, accessTokenExpiresAt);
  if (userId !== undefined && userId !== null) sessionStorage.setItem(USER_ID_KEY, String(userId));
  if (notifySession) notify("hortatech:session", { accessToken, accessTokenExpiresAt, userId });
};

export const clearStoredSession = () => {
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(ACCESS_EXPIRY_KEY);
  sessionStorage.removeItem(USER_ID_KEY);
  sessionStorage.removeItem("currentProjectId");
  sessionStorage.removeItem("currentProjectRole");
  sessionStorage.removeItem("currentProjectRoleStatus");
  sessionStorage.removeItem("frontendPermissions");
  notify("hortatech:session-expired");
};

const rawRequest = async (path, options = {}) => {
  const { body, headers = {}, token, ...fetchOptions } = options;
  const requestHeaders = { ...getDeviceHeaders(), ...headers };
  if (token) requestHeaders.Authorization = `Bearer ${token}`;
  if (body !== undefined && !(body instanceof FormData) && !requestHeaders["Content-Type"]) {
    requestHeaders["Content-Type"] = "application/json";
  }

  const response = await fetch(apiUrl(path), {
    credentials: "include",
    ...fetchOptions,
    headers: requestHeaders,
    body:
      body === undefined || body instanceof FormData || typeof body === "string"
        ? body
        : JSON.stringify(body),
  });
  const payload = await parseResponse(response);
  if (!response.ok) throw new ApiError(errorMessage(payload, response.status), response.status, payload);
  return payload;
};

let refreshInFlight;

export const refreshSession = async () => {
  if (!refreshInFlight) {
    refreshInFlight = rawRequest("/v1/refresh", { method: "POST" })
      .then((session) => {
        storeSession(session);
        return session;
      })
      .catch((error) => {
        // A rejected refresh credential ends the browser session. Transient
        // backend/upstream failures must not erase an otherwise recoverable one.
        if (error instanceof ApiError && error.status === 401) {
          clearStoredSession();
        }
        throw error;
      })
      .finally(() => {
        refreshInFlight = undefined;
      });
  }
  return refreshInFlight;
};

export const apiRequest = async (path, options = {}) => {
  const { skipRefresh = false, token = getStoredAccessToken(), ...requestOptions } = options;
  try {
    return await rawRequest(path, { ...requestOptions, token });
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 401 || skipRefresh) throw error;

    // Another request may already have refreshed while this request's stale
    // 401 was in flight. Reuse that token instead of rotating the cookie again.
    const currentToken = getStoredAccessToken();
    const accessToken = currentToken && currentToken !== token
      ? currentToken
      : (await refreshSession()).accessToken;

    // Retry through the raw boundary exactly once so another 401 cannot recurse.
    return rawRequest(path, { ...requestOptions, token: accessToken });
  }
};

export const register = (registration) =>
  rawRequest("/v1/register", {
    method: "POST",
    body: { ...registration, device: getDeviceInfo() },
  });

export const login = (credentials) =>
  rawRequest("/v1/login", {
    method: "POST",
    body: { ...credentials, device: getDeviceInfo() },
  }).then((session) => {
    // The screen first fetches /v1/profile, then publishes the authenticated
    // state so existing post-login UI does not render with a blank profile.
    storeSession(session, { notifySession: false });
    return session;
  });

export const validateAccount = (code) =>
  rawRequest(`/v1/validate/${encodeURIComponent(code)}`, { method: "GET" }).then((session) => {
    storeSession(session, { notifySession: false });
    return session;
  });

export const logout = async () => {
  const token = getStoredAccessToken();
  try {
    if (token) await rawRequest("/v1/logout", { method: "POST", token });
  } finally {
    clearStoredSession();
  }
};

export const getProfile = () => apiRequest("/v1/profile", { method: "GET" });

export const updateProfile = (profile) =>
  apiRequest("/v1/profile", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(profile),
  });

export const updatePhone = (phone) =>
  apiRequest("/v1/account/phone", { method: "PUT", body: { phone } });

export const changePassword = ({ currentPassword, newPassword }) =>
  apiRequest("/v1/account/password", { method: "PUT", body: { currentPassword, newPassword } });

export const changeEmail = (email) =>
  apiRequest("/v1/account/email", { method: "PUT", body: { email } });

export const getProjects = () => apiRequest("/v1/projects", { method: "GET" });

export const getProjectUsers = (projectId) =>
  apiRequest(`/v1/projects/${encodeURIComponent(projectId)}/users`, { method: "GET" });

export const inviteProjectUser = (projectId, invitation) =>
  apiRequest(`/v1/projects/${encodeURIComponent(projectId)}/users/invitations`, {
    method: "POST",
    body: invitation,
  });

export const updateProjectUser = (projectId, userId, change) =>
  apiRequest(`/v1/projects/${encodeURIComponent(projectId)}/users/${encodeURIComponent(userId)}`, {
    method: "PATCH",
    body: change,
  });

export const updateProjectUserProfile = (projectId, userId, profile) =>
  apiRequest(`/v1/projects/${encodeURIComponent(projectId)}/users/${encodeURIComponent(userId)}/profile`, {
    method: "PATCH",
    body: profile,
  });

export const verifyProjectUser = (projectId, userId) =>
  apiRequest(`/v1/projects/${encodeURIComponent(projectId)}/users/${encodeURIComponent(userId)}/verify`, {
    method: "POST",
  });

export const getProjectParcels = (projectId) =>
  apiRequest(`/v1/projects/${encodeURIComponent(projectId)}/parcels`, { method: "GET" });

export const getMyProjectParcels = (projectId) =>
  apiRequest(`/v1/projects/${encodeURIComponent(projectId)}/parcels/mine`, { method: "GET" });

export const createProjectParcel = (projectId, parcel) =>
  apiRequest(`/v1/projects/${encodeURIComponent(projectId)}/parcels`, {
    method: "POST",
    body: parcel,
  });

export const updateProjectParcel = (projectId, parcelId, parcel) =>
  apiRequest(`/v1/projects/${encodeURIComponent(projectId)}/parcels/${encodeURIComponent(parcelId)}`, {
    method: "PATCH",
    body: parcel,
  });

export const assignProjectParcel = (projectId, parcelId, userId) =>
  apiRequest(
    `/v1/projects/${encodeURIComponent(projectId)}/parcels/${encodeURIComponent(parcelId)}/users/${encodeURIComponent(userId)}`,
    { method: "PUT" }
  );

export const unassignProjectParcel = (projectId, parcelId, userId) =>
  apiRequest(
    `/v1/projects/${encodeURIComponent(projectId)}/parcels/${encodeURIComponent(parcelId)}/users/${encodeURIComponent(userId)}`,
    { method: "DELETE" }
  );

const withQuery = (path, params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.set(key, String(value));
    }
  });
  const queryString = query.toString();
  return queryString ? `${path}?${queryString}` : path;
};

export const getProjectSensors = (projectId, filters = {}) =>
  apiRequest(
    withQuery(`/v1/projects/${encodeURIComponent(projectId)}/sensors`, filters),
    { method: "GET" }
  );

export const getProjectMeasurements = (projectId, filters = {}) =>
  apiRequest(
    withQuery(`/v1/projects/${encodeURIComponent(projectId)}/measurements`, filters),
    { method: "GET" }
  );

export const getLatestProjectMeasurements = (projectId, filters = {}) =>
  apiRequest(
    withQuery(`/v1/projects/${encodeURIComponent(projectId)}/measurements/latest`, filters),
    { method: "GET" }
  );

export const getProjectEvents = (projectId, type = "all") =>
  apiRequest(
    `/v1/projects/${encodeURIComponent(projectId)}/events?type=${encodeURIComponent(type)}`,
    { method: "GET" }
  );

export const createProjectEvent = (projectId, event) =>
  apiRequest(`/v1/projects/${encodeURIComponent(projectId)}/events`, {
    method: "POST",
    body: event,
  });

export const updateProjectEvent = (projectId, eventId, event) =>
  apiRequest(
    `/v1/projects/${encodeURIComponent(projectId)}/events/${encodeURIComponent(eventId)}`,
    { method: "PATCH", body: event }
  );

export const deleteProjectEvent = (projectId, eventId) =>
  apiRequest(
    `/v1/projects/${encodeURIComponent(projectId)}/events/${encodeURIComponent(eventId)}`,
    { method: "DELETE" }
  );

export const requestPasswordReset = (email) =>
  rawRequest("/v1/password-reset/request", { method: "POST", body: { email } });

export const resetPassword = ({ resetToken, newPassword }) =>
  rawRequest("/v1/password-reset", { method: "POST", body: { resetToken, newPassword } });

export const uploadProfilePicture = (profilePic) => {
  const formData = new FormData();
  formData.append("profilePic", profilePic);
  return apiRequest("/v1/profile", { method: "PUT", body: formData });
};
